import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';

import { DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Text } from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper } from '../../../components';
import type { ParagraphTextMedia as Type } from '../../../types/uncinc_profile/paragraphs';
import useLayoutVariables, { useVariableImageAspectRatio } from '../../../hooks/useLayoutVariables';

import './style.scss';

interface ParagraphTextMediaProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphTextMedia: React.FC<ParagraphTextMediaProps> = ({
  id,
  entity,
}) => {
  const paragraphTextMedia = entity.toJS();

  const {
    forceTopAlignmentVideoAudio,
    mediaColClasses,
    mediaColClassesAlignmentRight,
    mediaColClassesAlignmentTop,
    textColClasses,
    textColClassesAlignmentLeft,
    textColClassesAlignmentTop,
  } = useLayoutVariables('paragraphTextMedia');

  const alignmentTopImageRatio = useVariableImageAspectRatio('paragraphTextMedia', [
    'alignmentTopImageRatioXS',
    'alignmentTopImageRatioSM',
    'alignmentTopImageRatioMD',
    'alignmentTopImageRatioLG',
    'alignmentTopImageRatioXL',
  ]);

  const alignmentLeftRightImageRatio = useVariableImageAspectRatio('paragraphTextMedia', [
    'alignmentLeftRightImageRatioXS',
    'alignmentLeftRightImageRatioSM',
    'alignmentLeftRightImageRatioMD',
    'alignmentLeftRightImageRatioLG',
    'alignmentLeftRightImageRatioXL',
  ]);

  const title = paragraphTextMedia.field_title[0]?.value;
  const body = paragraphTextMedia.field_body[0]?.processed || '';

  const link = paragraphTextMedia.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;

  const linkSecondary = paragraphTextMedia.field_link_secondary[0];
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri;

  const ratio = paragraphTextMedia.field_ratio[0]?.value;

  let alignment = paragraphTextMedia.field_alignment[0]?.value;
  // Per default, most images have a ratio of 16:9
  // But in many projects, we do a trick where the image is fitted next to the text,
  // depending on the height of the text (when the paragraph is left/right aligned).
  //
  // In those cases, we want to be able to set the `alignmentLeftRightImageRatio` to
  // for example 4:3, so you don't lose too much of your image when it's scaling.
  const imageRatio = alignment === 'top' ? alignmentTopImageRatio : alignmentLeftRightImageRatio;

  const media = paragraphTextMedia.field_media_item[0];

  let caption;
  if (media.bundle !== 'remote_video') {
    caption = media.entity.field_caption[0]?.value;
  }

  let posterImage;
  if (media.bundle === 'video') {
    posterImage = media.entity.field_poster_image[0]?.url;
  }

  if (
    (forceTopAlignmentVideoAudio && media.bundle !== 'image') ||
    (!title && !body)
  ) {
    alignment = 'top';
  }

  const containerClass = classNames(
    'Paragraph',
    'ParagraphTextMedia',
    `ParagraphTextMedia--alignment--${alignment}`,
    {
      'ParagraphTextMedia--flipped': alignment === 'left',
    },
  );

  const mediaClass = classNames(mediaColClasses, {
    [`${mediaColClassesAlignmentRight}`]: alignment === 'right',
    [`${mediaColClassesAlignmentTop}`]: alignment === 'top',
  });

  const bodyClass = classNames(textColClasses, {
    [`${textColClassesAlignmentLeft}`]: alignment === 'left',
    [`${textColClassesAlignmentTop}`]: alignment === 'top',
  });

  return (
    <div id={id} className={containerClass}>
      <Container>
        <div className="row">
          {(title || body || link || linkSecondary) && (
            <div className={bodyClass}>
              <div className="ParagraphTextMedia__body-wrapper">
                <div className="ParagraphTextMedia__body">
                  <Text
                    title={title}
                    text={body}
                  />
                </div>

                {(link || linkSecondary) && (
                  <div className="ParagraphTextMedia__links">
                    {linkUrl && (
                      <ButtonWrapper
                        className="ParagraphTextMedia__links__link"
                        type="primary"
                        href={linkUrl}
                      >
                        {linkTitle}
                      </ButtonWrapper>
                    )}
                    {linkSecondaryUrl && (
                      <ButtonWrapper
                        className="ParagraphTextMedia__links__link"
                        type="secondary"
                        href={linkSecondaryUrl}
                      >
                        {linkSecondaryTitle}
                      </ButtonWrapper>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={mediaClass}>
            <div className="ParagraphTextMedia__media-wrapper">
              <DynamicMediaItem
                media={media}
                caption={caption}
                imageAspectRatio={
                  ratio === 'default'
                    ? imageRatio
                    : 'original'
                }
                className="ParagraphTextMedia__media"
                posterImage={posterImage}
                cookieWrapper={false}
              />
            </div>
          </div>
        </div>

      </Container>
    </div>
  );
};

export default ParagraphTextMedia;
